<template>
  <div class="flex flex-col h-full min-h-0">
    <!-- Search field -->
    <div class="px-4 pt-1 pb-3 flex-none">
      <IconField>
        <InputIcon :class="isSearching ? 'pi pi-spin pi-spinner' : 'pi pi-search'" />
        <InputText
          ref="searchInput"
          v-model="term"
          class="w-full"
          placeholder="חיפוש לקוח/ה או אירוע"
          autocomplete="off" />
      </IconField>
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto px-4 pb-6">
      <!-- Idle -->
      <div v-if="!trimmedTerm" class="flex flex-col items-center justify-center text-center py-14 gap-2 text-gray-400">
        <i class="pi pi-search text-2xl" />
        <span class="text-sm">חפשו לקוח/ה לפי שם או טלפון, או אירוע לפי שם</span>
      </div>

      <!-- Loading skeletons -->
      <div v-else-if="isSearching && !results.length && !manualEvents.length" class="flex flex-col gap-3 pt-1">
        <div v-for="i in 3" :key="i" class="rounded-xl border border-gray-200 p-3 flex flex-col gap-3">
          <div class="flex items-center gap-3">
            <Skeleton shape="circle" size="2.5rem" />
            <div class="flex flex-col gap-1.5 flex-1">
              <Skeleton width="45%" height="0.9rem" borderRadius="0.4rem" />
              <Skeleton width="30%" height="0.7rem" borderRadius="0.4rem" />
            </div>
          </div>
          <Skeleton width="100%" height="2.5rem" borderRadius="0.6rem" />
        </div>
      </div>

      <!-- No matches -->
      <div v-else-if="!results.length && !manualEvents.length" class="flex flex-col items-center justify-center text-center py-14 gap-1.5 text-gray-500">
        <i class="pi pi-user text-2xl mb-1 text-gray-400" />
        <span class="text-sm font-medium">לא נמצאו תוצאות</span>
        <span class="text-xs text-gray-400">נסו שם אחר או מספר טלפון</span>
      </div>

      <!-- Results: customers (each with their appointments underneath), then manual events -->
      <div v-else class="flex flex-col gap-3 pt-1">
        <div
          v-for="result in results"
          :key="result.customer.id"
          class="rounded-xl border border-gray-200 bg-white overflow-hidden"
        >
          <button
            type="button"
            class="w-full flex items-center gap-3 px-3 py-2.5 text-start active:bg-gray-50"
            @click="openCustomer(result.customer.id)"
          >
            <span class="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[13px] font-semibold flex-none">
              {{ getTwoChars(result.customer.fullName) }}
            </span>
            <div class="flex-1 min-w-0">
              <div class="text-[13.5px] font-medium truncate">{{ result.customer.fullName }}</div>
              <div v-if="result.customer.phoneNumber" class="text-[11.5px] text-gray-500" style="text-align: start">
                {{ formatPhone(result.customer.phoneNumber) }}
              </div>
            </div>
            <i class="pi pi-chevron-left text-gray-300 !text-xs flex-none" />
          </button>

          <!-- Appointments for this customer: what's coming up first, then the last visits -->
          <div class="border-t border-gray-100">
            <div v-if="result.loading" class="px-3 py-2.5 flex flex-col gap-2">
              <Skeleton width="100%" height="1.6rem" borderRadius="0.5rem" />
              <Skeleton width="70%" height="1.6rem" borderRadius="0.5rem" />
            </div>
            <div v-else-if="!result.appointments.length" class="px-3 py-2.5 text-[11.5px] text-gray-400">
              אין תורים ללקוח/ה
            </div>
            <template v-else>
              <button
                v-for="appointment in result.appointments"
                :key="appointment.appointmentId"
                type="button"
                class="w-full flex items-center gap-2.5 px-3 py-2 text-start border-b border-gray-50 last:border-b-0 active:bg-gray-50"
                @click="openAppointment(result.customer.id, appointment)"
              >
                <span class="w-1.5 h-1.5 rounded-full flex-none" :class="statusDotClass(appointment)" />
                <span class="text-[12.5px] font-medium flex-none">{{ formatDate(appointment.date) }}</span>
                <span class="text-[12px] text-gray-500 flex-none" style="direction: ltr">{{ appointment.startHour }}</span>
                <span class="text-[12px] text-gray-500 truncate flex-1">{{ servicesLabel(appointment) }}</span>
                <span class="text-[10.5px] flex-none px-1.5 py-0.5 rounded-full" :class="statusChipClass(appointment)">
                  {{ statusLabel(appointment) }}
                </span>
              </button>
              <button
                v-if="result.hasMore"
                type="button"
                class="w-full text-center text-[11.5px] text-primary py-2"
                @click="openCustomer(result.customer.id)"
              >כל התורים של {{ result.customer.fullName }}</button>
            </template>
          </div>
        </div>

        <div v-if="manualEvents.length" class="flex flex-col gap-1.5" :class="{ 'pt-1': results.length }">
          <span class="text-[11.5px] font-medium text-gray-500 px-1">אירועים</span>
          <div class="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <button
              v-for="event in manualEvents"
              :key="event.id"
              type="button"
              class="w-full flex items-center gap-2.5 px-3 py-2.5 text-start border-b border-gray-50 last:border-b-0 active:bg-gray-50"
              @click="openManualEvent(event)"
            >
              <span
                class="w-8 h-8 rounded-lg flex items-center justify-center flex-none"
                :style="{ backgroundColor: `${event.serviceColor || '#9ca3af'}22`, color: event.serviceColor || '#6b7280' }"
              >
                <i class="pi pi-calendar !text-xs" />
              </span>
              <div class="flex-1 min-w-0">
                <div class="text-[13px] font-medium truncate">{{ event.title || 'אירוע' }}</div>
                <div class="text-[11.5px] text-gray-500 flex items-center gap-1.5">
                  <span>{{ formatDate(event.start.split(' ')[0]) }}</span>
                  <span style="direction: ltr">{{ event.start.split(' ')[1] }}–{{ event.end.split(' ')[1] }}</span>
                  <span v-if="event.recurringManualEventId" class="pi pi-replay !text-[10px] text-gray-400" />
                </div>
              </div>
              <span
                v-if="isUpcomingEvent(event)"
                class="text-[10.5px] flex-none px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-500"
              >עתידי</span>
              <i class="pi pi-chevron-left text-gray-300 !text-xs flex-none" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AppointmentStatus, AppointmentType, CustomerTimelineType, ICalendarEvent, ICustomer, ICustomerTimeline } from '@torim-app/types';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import { useRouter } from 'vue-router';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Skeleton from 'primevue/skeleton';
import { getTwoChars } from '@torim-app/utils';
import { AppointmentsApi, CustomersApi } from '@app/api/admin';
import { useAdminHomeStore, useAdminStore } from '@app/stores';
import ExistingEventOptions from '@app/components/admin/modals/ExistingEventOptions.vue';
import { AdminRouteNames } from '@app/router/route-names';
import { useDrawer } from '@torim-app/shared-components';
import { trackEvent } from '@app/composables/posthog';

type SearchResult = {
  customer: Pick<ICustomer, 'id' | 'fullName' | 'tags' | 'loyalty' | 'smsOptedOut' | 'phoneNumber'>;
  appointments: ICustomerTimeline[];
  loading: boolean;
  hasMore: boolean;
};

/** Wait this long after the last keystroke before hitting the server. */
const DEBOUNCE_MS = 400;
/** Customers per search, and appointments shown under each — enough to recognise, not to scroll. */
const MAX_CUSTOMERS = 6;
const MAX_APPOINTMENTS_PER_CUSTOMER = 3;

const router = useRouter();
const adminStore = useAdminStore();
const adminHomeStore = useAdminHomeStore();
const { openDrawer, closeDrawer } = useDrawer();

const term = ref('');
const results = ref<SearchResult[]>([]);
const manualEvents = ref<ICalendarEvent[]>([]);
const isSearching = ref(false);
const searchInput = ref<{ $el?: HTMLElement } | null>(null);

const trimmedTerm = computed(() => term.value.trim());

let debounceTimer: ReturnType<typeof setTimeout> | undefined;
// Guards against out-of-order responses: only the newest search may write results.
let requestId = 0;

const runSearch = async () => {
  const query = trimmedTerm.value;
  const currentRequest = ++requestId;

  if (!query) {
    results.value = [];
    manualEvents.value = [];
    isSearching.value = false;
    return;
  }

  isSearching.value = true;
  // Manual events are an independent lookup — they fill in beside the customers and
  // never block or fail the customer search.
  AppointmentsApi.searchManualEvents(query)
    .then(events => {
      if (currentRequest === requestId) manualEvents.value = events;
    })
    .catch(e => {
      console.error('Manual event search failed:', e);
      if (currentRequest === requestId) manualEvents.value = [];
    });

  try {
    const { customers } = await CustomersApi.fetchCustomers({ search: query, limit: MAX_CUSTOMERS });
    if (currentRequest !== requestId) return;

    results.value = customers.map(customer => ({ customer, appointments: [], loading: true, hasMore: false }));
    isSearching.value = false;

    // Appointments are per customer, so they load in parallel behind the names — the
    // list is already useful before they arrive.
    await Promise.all(customers.map(async (customer, index) => {
      try {
        const timeline = await CustomersApi.fetchCustomerHistory(customer.id);
        if (currentRequest !== requestId) return;
        const ordered = orderAppointments(timeline);
        results.value[index] = {
          customer,
          appointments: ordered.slice(0, MAX_APPOINTMENTS_PER_CUSTOMER),
          loading: false,
          hasMore: ordered.length > MAX_APPOINTMENTS_PER_CUSTOMER,
        };
      } catch (e) {
        console.error('Failed to load customer appointments:', e);
        if (currentRequest !== requestId) return;
        results.value[index] = { customer, appointments: [], loading: false, hasMore: false };
      }
    }));
  } catch (e) {
    console.error('Customer search failed:', e);
    if (currentRequest !== requestId) return;
    results.value = [];
    isSearching.value = false;
  }
};

/**
 * Upcoming visits first (soonest first — that's what an owner is usually looking for),
 * then past ones most-recent first.
 */
const orderAppointments = (timeline: ICustomerTimeline[]): ICustomerTimeline[] => {
  const key = (a: ICustomerTimeline) => DateTime.fromISO(a.date).toMillis();
  const future = timeline
    .filter(a => a.type === CustomerTimelineType.FUTURE)
    .sort((a, b) => key(a) - key(b));
  const past = timeline
    .filter(a => a.type !== CustomerTimelineType.FUTURE)
    .sort((a, b) => key(b) - key(a));
  return [...future, ...past];
};

watch(term, () => {
  if (debounceTimer) clearTimeout(debounceTimer);
  // An empty field clears immediately — no point waiting to show nothing.
  if (!trimmedTerm.value) {
    requestId++;
    results.value = [];
    manualEvents.value = [];
    isSearching.value = false;
    return;
  }
  isSearching.value = true;
  debounceTimer = setTimeout(() => {
    trackEvent('home_search_performed');
    runSearch();
  }, DEBOUNCE_MS);
});

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer);
});

onMounted(() => {
  // Open with the keyboard ready — the drawer exists to be typed into.
  const el = searchInput.value?.$el as HTMLInputElement | undefined;
  el?.focus?.();
});

const serviceNames = computed<Record<string, string>>(() =>
  Object.fromEntries(adminStore.getServices.map(s => [s.id, s.name]))
);

const servicesLabel = (appointment: ICustomerTimeline): string =>
  (appointment.servicesIds ?? [])
    .map(id => serviceNames.value[id])
    .filter(Boolean)
    .join(', ') || 'ללא שירות';

const formatDate = (date: string) => DateTime.fromISO(date).setLocale('he').toFormat('dd/MM/yy');

const formatPhone = (phoneNumber: string) =>
  `${phoneNumber.substring(0, 3)}-${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6, 10)}`;

const isUpcoming = (appointment: ICustomerTimeline) =>
  appointment.type === CustomerTimelineType.FUTURE
  && ([AppointmentStatus.VERIFIED, AppointmentStatus.APPROVED] as AppointmentStatus[]).includes(appointment.status);

const statusLabel = (appointment: ICustomerTimeline): string => {
  if (appointment.status === AppointmentStatus.CANCELLED) return 'בוטל';
  if (appointment.status === AppointmentStatus.NO_SHOW) return 'לא הגיע/ה';
  return isUpcoming(appointment) ? 'עתידי' : 'הושלם';
};

const statusChipClass = (appointment: ICustomerTimeline): string => {
  if (appointment.status === AppointmentStatus.CANCELLED) return 'bg-red-50 text-red-500';
  if (appointment.status === AppointmentStatus.NO_SHOW) return 'bg-orange-50 text-orange-500';
  return isUpcoming(appointment) ? 'bg-blue-50 text-blue-500' : 'bg-green-50 text-green-600';
};

const statusDotClass = (appointment: ICustomerTimeline): string => {
  if (appointment.status === AppointmentStatus.CANCELLED) return 'bg-red-400';
  if (appointment.status === AppointmentStatus.NO_SHOW) return 'bg-orange-400';
  return isUpcoming(appointment) ? 'bg-primary' : 'bg-green-500';
};

const openCustomer = (customerId: string) => {
  trackEvent('home_search_customer_opened');
  closeDrawer();
  router.push({ name: AdminRouteNames.ClientProfile, params: { id: customerId } });
};

const isUpcomingEvent = (event: ICalendarEvent) =>
  DateTime.fromFormat(event.start, 'yyyy-MM-dd HH:mm') >= DateTime.local();

// The search drawer closes first so the event opens as the primary drawer — its own
// sub-drawers (edit times, recurring) need the secondary slot. The delay outlasts the
// drawer's close animation, which clears the primary slot when it ends.
const openManualEvent = (event: ICalendarEvent) => {
  trackEvent('home_search_manual_event_opened');
  closeDrawer();
  setTimeout(() => {
    openDrawer(ExistingEventOptions, {
      apid: event.id,
      date: DateTime.fromFormat(event.start, 'yyyy-MM-dd HH:mm').toJSDate(),
      onClose: (shouldReload?: boolean) => {
        closeDrawer();
        if (shouldReload) adminHomeStore.fetchBusinessCalendarDataSilently();
      }
    }, {
      title: event.type === AppointmentType.OUT_OF_OFFICE ? 'יום ללא עבודה' : 'פרטי התור',
      noPadding: true,
      full: false
    });
  }, 350);
};

// The customer profile highlights and scrolls to `apid`, so one push lands the owner
// straight on the visit they searched for.
const openAppointment = (customerId: string, appointment: ICustomerTimeline) => {
  trackEvent('home_search_appointment_opened');
  closeDrawer();
  router.push({
    name: AdminRouteNames.ClientProfile,
    params: { id: customerId },
    query: { apid: appointment.appointmentId },
  });
};
</script>

<style scoped lang="scss"></style>
